"use client";

import { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Pagination,
  Button,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import axios from "axios";
import AccommodationCard from "./AccommodationCard";
import FilterModal from "./FilterModal";
import StyledTitleDot from "../../components/common/StyledTitleDot";
import { useTranslation } from "react-i18next";

const initialStateFilters = {
  residence: "",
  priceRange: [0, 100],
  roomType: [],
  provider: "",
  facilities: [],
  duration: "",
  university: "",
  roomFeatures: [],
  checkIn: "",
  checkOut: "",
};
export default function SpecialOffers() {
  const { t } = useTranslation();

  const [accommodations, setAccommodations] = useState([]);
  const [filteredAccommodations, setFilteredAccommodations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  const [filters, setFilters] = useState(initialStateFilters);

  const theme = useTheme();

  const isXl = useMediaQuery(theme.breakpoints.up("xl"));

  const [itemsPerPage, setItemsPerPage] = useState(9);

  useEffect(() => {
    setItemsPerPage(isXl ? 12 : 9);
  }, [isXl]);

  useEffect(() => {
    setTotalPages(Math.ceil(filteredAccommodations.length / itemsPerPage));
    setPage(1);
  }, [itemsPerPage, filteredAccommodations]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.append("api_key", "O0AUQ0KLAD3I71K8YKR82Y6AP");

      const response = await axios.post(
        "https://webservice.londonist.co.uk/v1/DMCGetAvailableResidencesGAP",
        params,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      const processedData = processAccommodationData(response.data.result);
      setAccommodations(processedData);
      setFilteredAccommodations(processedData);
      setTotalPages(Math.ceil(processedData.length / itemsPerPage));
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch data. Please try again later.");
      setLoading(false);
      console.error("Error fetching data:", err);
    }
  };

  useEffect(() => {
    fetchData();

    // For demo purposes, we'll use the provided data directly
    const processedData = processAccommodationData(demoData);
    setAccommodations(processedData);
    setFilteredAccommodations(processedData);
    setTotalPages(Math.ceil(processedData.length / itemsPerPage));

    // Uncomment this to fetch from actual API
  }, []);

  // Function to process the nested data structure into a flat array
  const processAccommodationData = (data) => {
    const flattenedData = [];

    Object.keys(data).forEach((provider) => {
      Object.keys(data[provider]).forEach((propertyName) => {
        const property = data[provider][propertyName];

        Object.keys(property.room_types).forEach((roomTypeName) => {
          const roomType = property.room_types[roomTypeName];

          roomType.available_rooms.forEach((roomGroup) => {
            roomGroup.forEach((room) => {
              flattenedData.push({
                provider,
                propertyName: property.property_name,
                propertyAddress: property.property_address,
                propertyImage: property.property_image,
                propertyFacilities: property.property_facilities || [],
                propertyNearByUniversities:
                  property.property_near_by_universities || [],
                propertyDescription: property.property_description || "",
                roomTypeName,
                roomKind: roomType.room_kind,
                roomImages: roomType.room_images,
                description: roomType.description,
                bedCount: roomType.bed_count,
                dailyPrice: Number.parseFloat(room.daily_price),
                normalDailyPrice: Number.parseFloat(room.normal_daily_price),
                totalPrice: Number.parseFloat(room.total_price),
                duration: Number.parseInt(room.duration),
                checkin: room.checkin,
                checkout: room.checkout,
                roomId: room.room_id,
                id: room.id,
              });
            });
          });
        });
      });
    });

    return flattenedData;
  };

  // Apply filters function
  const applyFilters = (filters) => {
    let filtered = [...accommodations];

    // Residence filter (extract from description or address)
    if (filters.residence) {
      filtered = filtered.filter((acc) =>
        acc.propertyName?.includes(filters.residence)
      );
    }

    // Price range filter
    if (filters.priceRange[0] > 0 || filters.priceRange[1] < 100) {
      filtered = filtered.filter(
        (acc) =>
          acc.dailyPrice >= filters.priceRange[0] &&
          acc.dailyPrice <= filters.priceRange[1]
      );
    }

    // Room type filter
    if (filters.roomType.length > 0) {
      filtered = filtered.filter((acc) =>
        filters.roomType.some(
          (type) =>
            acc.roomKind?.toLowerCase().includes(type.toLowerCase()) ||
            acc.roomTypeName?.toLowerCase().includes(type.toLowerCase())
        )
      );
    }

    // Provider filter
    if (filters.provider) {
      filtered = filtered.filter((acc) => acc.provider === filters.provider);
    }

    // Facilities filter
    if (filters.facilities.length > 0) {
      filtered = filtered.filter((acc) =>
        filters.facilities.some((facility) =>
          acc.propertyFacilities?.some((propFacility) =>
            propFacility.toLowerCase().includes(facility.toLowerCase())
          )
        )
      );
    }

    // Duration filter
    if (filters.duration) {
      filtered = filtered.filter((acc) => {
        const duration = acc.duration;
        switch (filters.duration) {
          case "short":
            return duration >= 1 && duration <= 7;
          case "medium":
            return duration >= 8 && duration <= 14;
          case "long":
            return duration >= 15;
          default:
            return true;
        }
      });
    }

    // University filter (check in description)
    if (filters.university) {
      filtered = filtered.filter((acc) =>
        acc.propertyNearByUniversities.some(
          (item) => item?.university_title === filters.university
        )
      );
    }

    // Room features filter
    if (filters.roomFeatures.length > 0) {
      filtered = filtered.filter((acc) =>
        filters.roomFeatures.some((feature) =>
          acc.description?.toLowerCase().includes(feature.toLowerCase())
        )
      );
    }

    if (filters.checkIn && filters.checkOut) {
      filtered = filtered.filter((acc) => {
        const accCheckIn = new Date(acc.checkin);
        const accCheckOut = new Date(acc.checkout);
        const selectedCheckIn = new Date(filters.checkIn);
        const selectedCheckOut = new Date(filters.checkOut);

        return accCheckIn <= selectedCheckIn && accCheckOut >= selectedCheckOut;
      });
    } else if (filters.checkIn) {
      filtered = filtered.filter(
        (acc) => new Date(acc.checkin) <= new Date(filters.checkIn)
      );
    } else if (filters.checkOut) {
      filtered = filtered.filter(
        (acc) => new Date(acc.checkout) >= new Date(filters.checkOut)
      );
    }

    setFilteredAccommodations(filtered);
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
    setPage(1);
    setActiveFilters(filters);

    // Check if any filters are active
    const hasFilters = Object.values(filters).some((value) => {
      if (Array.isArray(value)) {
        // Price range gibi 2'li array kontrolü burada yapılmalı
        if (
          value.length === 2 &&
          typeof value[0] === "number" &&
          typeof value[1] === "number"
        ) {
          return value[0] > 0 || value[1] < 100;
        }
        return value.length > 0; // Diğer array'ler (örneğin roomType, facilities) için
      }
      if (typeof value === "string") return value.trim() !== "";
      return false;
    });

    if (!hasFilters) {
      setFilters(initialStateFilters);
    }
    setHasActiveFilters(hasFilters);
  };

  // Remove specific filter function ekle
  const removeSpecificFilter = (filterKey) => {
    const newFilters = { ...activeFilters };

    switch (filterKey) {
      case "residence":
        newFilters.residence = "";
        break;
      case "provider":
        newFilters.provider = "";
        break;
      case "duration":
        newFilters.duration = "";
        break;
      case "university":
        newFilters.university = "";
        break;
      case "roomType":
        newFilters.roomType = [];
        break;
      case "facilities":
        newFilters.facilities = [];
        break;
      case "roomFeatures":
        newFilters.roomFeatures = [];
        break;
      case "price":
        newFilters.priceRange = [0, 100];
        break;
      case "checkIn":
        newFilters.checkIn = "";
        break;
      case "checkOut":
        newFilters.checkOut = "";
        break;
      default:
        break;
    }

    applyFilters(newFilters);
    setFilters(newFilters);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilteredAccommodations(accommodations);
    setTotalPages(Math.ceil(accommodations.length / itemsPerPage));
    setPage(1);
    setActiveFilters({});
    setHasActiveFilters(false);
    setFilters(initialStateFilters);
  };

  // Get current page items
  const getCurrentItems = () => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAccommodations.slice(startIndex, endIndex);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Get active filter chips
  const getActiveFilterChips = () => {
    const chips = [];

    if (activeFilters.residence) {
      chips.push({
        label: `${t("residence")}: ${activeFilters.residence}`,
        key: "residence",
      });
    }
    if (activeFilters.provider) {
      chips.push({
        label: `${t("provider")}: ${activeFilters.provider}`,
        key: "provider",
      });
    }
    if (activeFilters.duration) {
      const durationLabels = {
        short: t("shortStays"),
        medium: t("mediumStays"),
        long: t("longStays"),
      };
      chips.push({
        label: durationLabels[activeFilters.duration],
        key: "duration",
      });
    }
    if (activeFilters.university) {
      chips.push({
        label: `${t("near")}: ${activeFilters.university}`,
        key: "university",
      });
    }
    if (activeFilters.roomType?.length > 0) {
      chips.push({
        label: `${t("room")}: ${activeFilters.roomType.join(", ")}`,
        key: "roomType",
      });
    }
    if (activeFilters.facilities?.length > 0) {
      chips.push({
        label: `${t("facilities")}: ${activeFilters.facilities.length} selected`,
        key: "facilities",
      });
    }
    if (
      activeFilters.priceRange &&
      (activeFilters.priceRange[0] > 0 || activeFilters.priceRange[1] < 100)
    ) {
      chips.push({
        label: `${t("price")}: £${activeFilters.priceRange[0]}-£${activeFilters.priceRange[1]}`,
        key: "price",
      });
    }

    return chips;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Header with Filter Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography textTransform={"capitalize"} className="title" variant="h4">
          {t("studentAccommodations")}
          <StyledTitleDot />
        </Typography>
        <Button
          variant="outlined"
          startIcon={<FilterIcon />}
          onClick={() => setFilterModalOpen(true)}
          sx={{ minWidth: 120, textTransform: 'capitalize' }}
        >
          {t("filters")}
        </Button>
      </Box>

      {/* Results Summary */}
      <Box sx={{ mb: 3 }}>
        <Typography textTransform={"initial"} variant="body1" color="text.secondary">
          {t("accommodationListInfo", {
            count: getCurrentItems().length,
            total: filteredAccommodations.length,
          })}
        </Typography>
      </Box>

      {/* Active Filters */}
      {hasActiveFilters && (
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {getActiveFilterChips().map((chip, index) => (
              <Chip
                sx={{ textTransform: 'capitalize' }}
                key={index}
                label={chip.label}
                onDelete={() => removeSpecificFilter(chip.key)}
                color="primary"
                variant="outlined"
              />
            ))}
            <Chip
              onClick={() => clearAllFilters()}
              sx={{ textTransform: 'capitalize' }}
              label={t("clearAll")}
              onDelete={clearAllFilters}
              color="primary"
              variant="outlined"
            />
            {/*    <Button
              variant="outlined"
              size="small"
              startIcon={
                <ClearIcon
                  sx={{
                    color: theme.palette.primary.main,
                  }}
                />
              }
              onClick={clearAllFilters}
              sx={{ ml: 1, textTransform: 'capitalize' }}
            >
              {t("clearAll")}
            </Button> */}
          </Stack>
        </Box>
      )}

      {/* Accommodation Grid */}

      {/* Accommodation Grid */}
      <Grid container spacing={3}>
        {(() => {
          const groupedByResidence = getCurrentItems().reduce((acc, item) => {
            const key = item.propertyName;
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);
            return acc;
          }, {});

          const cards = [];

          Object.entries(groupedByResidence).forEach(([residence, group]) => {
            group.forEach((accommodation, idx) => {
              const totalImages =
                accommodation.roomImages?.length ||
                accommodation.propertyImage?.length ||
                1;

              cards.push(
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  xl={3}
                  key={`${accommodation.roomId}-${idx}`}
                >
                  <AccommodationCard
                    accommodation={accommodation}
                    indexAmongSameResidences={idx % totalImages}
                    filters={activeFilters}
                  />
                </Grid>
              );
            });
          });

          return cards;
        })()}
      </Grid>

      {/* <Grid container spacing={3}>
        {getCurrentItems().map((accommodation, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            key={`${accommodation.roomId}-${index}`}
          >
            <AccommodationCard accommodation={accommodation} />
          </Grid>
        ))}
      </Grid> */}

      {loading ? (
        <CircularProgress
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />
      ) : null}

      {/* No Results Message */}
      {filteredAccommodations.length === 0 && !loading && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography textTransform={"capitalize"} variant="h6" color="text.secondary" gutterBottom>
            {t("noAccommodationsFound")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("tryAdjustingYourFiltersToSeeMoreResults")}
          </Typography>
          <Button variant="outlined" onClick={clearAllFilters}>
            {t("clearAllFilters")}
          </Button>
        </Box>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Filter Modal */}
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onApplyFilters={applyFilters}
        accommodations={accommodations}
        filters={filters}
        setFilters={setFilters}
        initialStateFilters={initialStateFilters}
      />
    </>
  );
}

// This is just for demo purposes - in a real app, you'd fetch this from the API
const demoData = {
  // The API response data would be used here
};
